import {
	DEFAULT_INVENTORY_CATEGORIES,
	filterTablesForSection,
	filterTimesByService,
	hasBookableServiceSlot,
	pickServiceSlotTime,
	serviceSlots,
	tableOffersService,
	TDR
} from 'tdr-common';
import { SearchFilters } from '../../../routes/AvailabilityGuard';
import { TableCategories } from '../../inventory-carousel/categorizeTables';

export type InventorySectionCategories = TableCategories & { groupSizeConflict: TDR.Table[] };

export type InventoryEmptyState = { show: boolean; isLoading: boolean; tables: TDR.Table[] };

/** The inputs a section needs to work out which tables it owns. */
export type InventoryResolveArgs = {
	categories: InventorySectionCategories;
	searchFilters: SearchFilters;
	emptyState: InventoryEmptyState;
	/** Per-table slots for the searched date; required for a service-scoped section. */
	timeslotDocMap?: Record<string, { timeSlots?: TDR.TimeSlotMap }>;
	/**
	 * The other times the availability service found for a table. Only consulted for the
	 * `x-sell-time` category of a service-scoped section, which must not claim a table whose
	 * alternatives all belong to another service.
	 */
	availableTimesFor?: (tableId: string) => string[] | undefined;
};

export type ResolvedInventorySection = {
	/** Categories in render order. */
	order: TDR.SearchConfig.InventoryCategory[];
	/** This section's tables for a category, already narrowed by its table filter. */
	tablesFor: (category: TDR.SearchConfig.InventoryCategory) => TDR.Table[];
	/** Open-parameters empty-state tables, narrowed by the same filter. */
	emptyStateTables: TDR.Table[];
	/** Cards this section will render — i.e. the position offset the next one starts at. */
	cardCount: number;
	/**
	 * The start time a table's card represents in this section. Undefined for a
	 * time-agnostic section, where the card keeps showing whatever the guest searched.
	 */
	slotTimeFor: (tableId: string) => string | undefined;
	/**
	 * The times a cross-sell card may advertise here — narrowed to this section's services so a
	 * Full Day section never offers a Half Day start. Passes through unchanged when unscoped.
	 */
	crossSellTimesFor: (tableId: string, availableTimes: string[] | undefined) => string[];
};

/**
 * Everything about WHICH tables an inventory section shows, in one place.
 *
 * InventorySection renders from this and ResultsSections counts from it, so the cards on
 * screen and the analytics positions can never disagree about what a section contains.
 *
 * It lives in its own module rather than beside the component deliberately: ResultsSections
 * needs the counts even in tests that stub the component out, and a stubbed module exports
 * no helpers.
 */
export function resolveInventorySection(
	section: TDR.SearchConfig.InventorySection,
	args: InventoryResolveArgs
): ResolvedInventorySection {
	const { categories, searchFilters, emptyState, timeslotDocMap, availableTimesFor } = args;
	const order = section.categories?.length ? section.categories : DEFAULT_INVENTORY_CATEGORIES;

	const slotsOf = (tableId: string) => timeslotDocMap?.[tableId]?.timeSlots;

	// A service-scoped section claims only the tables that run one of its services on this date.
	// With no slot data loaded there is nothing to match on, and guessing either way is wrong —
	// claiming every table would duplicate the whole estate across sibling sections — so claim none
	// and let the section hide until the data arrives.
	const offersService = (table: TDR.Table) =>
		!section.services?.length || tableOffersService(slotsOf(table.id), section.services);

	const applyFilters = (tables: TDR.Table[]) =>
		filterTablesForSection(tables, section.tables).filter(offersService);

	const crossSellTimesFor = (tableId: string, availableTimes: string[] | undefined) => {
		const slots = slotsOf(tableId);
		// An ABSENT list means the availability service told us nothing — fall back to the service's
		// own bookable slots rather than concluding there is nothing to advertise. An EMPTY list is
		// a real answer and is respected: it means nothing is bookable.
		const offered = availableTimes
			?? serviceSlots(slots, section.services).filter(entry => entry.slot.available).map(entry => entry.time);
		return filterTimesByService(offered, slots, section.services);
	};

	const unfiltered = (category: TDR.SearchConfig.InventoryCategory): TDR.Table[] => {
		switch (category) {
			case 'available': return categories.available;
			case 'x-sell-time': return categories.availableAtOtherTimes;
			case 'sold-out': return searchFilters.date ? categories.dateSoldOut : [];
			case 'x-sell-dates': return searchFilters.date ? categories.dateClosed : [];
			case 'x-sell-groupsize': return categories.groupSizeConflict;
			default: return [];
		}
	};

	/**
	 * Tables bookable somewhere on this date, in the page's existing order.
	 *
	 * A service-scoped section re-decides `available` vs `x-sell-time` for itself, because the
	 * availability service answers "bookable at the searched time?" for the table as a whole while
	 * the section is asking about one package. Those disagree constantly: a table whose Full Day
	 * slot is free at 09:00 is "available", but inside the Half Day section it is only bookable at
	 * 09:15 — the same answer as a neighbour the service called a time conflict. Judging both from
	 * the scoped slot is what stops two cards advertising 9:15 wearing different badges.
	 */
	const bookableSomewhere = () => {
		const seen = new Set<string>();
		return [...categories.available, ...categories.availableAtOtherTimes].filter(table => {
			if (seen.has(table.id)) {
				return false;
			}
			seen.add(table.id);
			return true;
		});
	};

	/** This section's start time for a table, when that slot is actually bookable. */
	const bookableScopedTime = (tableId: string) => {
		const slots = slotsOf(tableId);
		return hasBookableServiceSlot(slots, section.services)
			? pickServiceSlotTime(slots, section.services, searchFilters.time)
			: undefined;
	};

	// Filter once per category rather than on every lookup — callers read these repeatedly.
	const byCategory = new Map<TDR.SearchConfig.InventoryCategory, TDR.Table[]>(
		order.map(category => {
			const scopedSplit = !!section.services?.length
				&& (category === 'available' || category === 'x-sell-time');
			if (!scopedSplit) {
				return [category, applyFilters(unfiltered(category))];
			}
			const wantSearchedTime = category === 'available';
			return [
				category,
				applyFilters(bookableSomewhere()).filter(table => {
					const time = bookableScopedTime(table.id);
					if (!time) {
						return false;
					}
					// `available` is a promise: the card reads "Available at <time>" and books it.
					// Anything else is "bookable at another time" — the cross-sell card.
					const isSearched = time === searchFilters.time;
					if (wantSearchedTime !== isSearched) {
						return false;
					}
					// A cross-sell card must also have a time to advertise in THIS service.
					return isSearched || crossSellTimesFor(table.id, availableTimesFor?.(table.id)).length > 0;
				})
			];
		})
	);
	const tablesFor = (category: TDR.SearchConfig.InventoryCategory) => byCategory.get(category) ?? [];
	const emptyStateTables = applyFilters(emptyState.tables);

	const slotTimeFor = (tableId: string) =>
		section.services?.length
			? pickServiceSlotTime(slotsOf(tableId), section.services, searchFilters.time)
			: undefined;

	const cardCount = emptyState.show
		? emptyStateTables.length
		: order.reduce((total, category) => total + tablesFor(category).length, 0);

	return { order, tablesFor, emptyStateTables, cardCount, slotTimeFor, crossSellTimesFor };
}
